import { useState, useEffect, useRef } from 'react';
import { readOrgFile, verifyRootHandle, clearRootHandle } from './storage/FileSystemAdapter';
import { runEvidenceMigrationIfNeeded } from './storage/evidenceMigration';
import { runDateFormatMigrationIfNeeded } from './storage/dateFormatMigration';
import ConfirmDialog from './components/shared/ConfirmDialog';
import SetupScreen from './components/SetupScreen';
import OrgSelector from './components/OrgSelector';
import Dashboard from './components/Dashboard';
import RiskList from './components/risks/RiskList';
import SoAView from './components/soa/SoAView';
import RTPView from './components/rtp/RTPView';
import SettingsView from './components/settings/SettingsView';
import DocumentsView from './components/documents/DocumentsView';
import AuditsView from './components/audits/AuditsView';
import ContextView from './components/context/ContextView';
import CustomersView from './components/customers/CustomersView';
import ScopeView from './components/scope/ScopeView';
import ObjectivesView from './components/objectives/ObjectivesView';
import ManagementReviewsView from './components/management-reviews/ManagementReviewsView';
import NonconformitiesView from './components/nonconformities/NonconformitiesView';
import AssetsView from './components/assets/AssetsView';
import SuppliersView from './components/suppliers/SuppliersView';
import IncidentsView from './components/incidents/IncidentsView';
import CompetenceView from './components/competence/CompetenceView';
import AwarenessView from './components/awareness/AwarenessView';
import MetricsView from './components/metrics/MetricsView';
import LegalRegisterView from './components/legal-register/LegalRegisterView';
import AuthoritiesView from './components/authorities/AuthoritiesView';
import SpecialInterestGroupsView from './components/special-interest-groups/SpecialInterestGroupsView';
import ExecutiveDashboardView from './components/executive/ExecutiveDashboardView';
import Sidebar from './components/shared/Sidebar';
import { BG_CONTENT, TEXT_MUTED, BORDER_DEFAULT, TEXT_HEADING, BRAND_PRIMARY, BG_SHELL, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY, FONT_TINY } from './theme';
import { NAVBAR_HEIGHT } from './utils/constants';

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');`;

// ─── AI COMPONENT STYLES ──────────────────────────────────────────────────────
// Injected once at the root so .ai-spark and .ai-suggest-text are always
// available regardless of which route is active.
const AI_STYLES = `
  @keyframes ai-gradient-flow {
    0%   { background-position: 0% center; }
    100% { background-position: 200% center; }
  }
  .ai-suggest-text {
    background: linear-gradient(90deg, #7c3aed, ${TEXT_HEADING}, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ai-gradient-flow 3s ease infinite;
  }
  .ai-suggest-btn:hover .ai-suggest-text {
    opacity: 0.75;
  }
  .ai-suggest-btn:hover {
    opacity: 0.75;
    transform: scale(1.02);
  }
  @keyframes ai-spark-flow {
    0%   { background-position: 0% center; }
    100% { background-position: 200% center; }
  }
  .ai-spark {
    background: linear-gradient(90deg, #7c3aed, ${TEXT_HEADING}, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ai-spark-flow 3s ease infinite;
    font-size: 1em;
    margin-left: 0.3em;
    display: inline-block;
    vertical-align: baseline;
  }
`;

// ─── LAYOUT CONSTANTS ─────────────────────────────────────────────────────────
// Single unified content width applied to every module wrapper. Wide enough
// to fit the widest tables (SoA, RTP, risk list) without horizontal scroll
// on a standard 1440px desktop screen. Apply this constant to every module —
// no exceptions, including narrow form-based modules (context, scope, policy,
// settings) — so the content area never shifts as the user navigates.
export const CONTENT_MAX_WIDTH = 1200;

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
function Navbar({ lang, onLangToggle, orgName }) {
  return (
    <div style={{
      height: NAVBAR_HEIGHT,
      background: BG_SHELL,
      borderBottom: `1px solid ${BORDER_DEFAULT}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 1.5rem',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      boxSizing: 'border-box',
    }}>
      {/* Branding + active org */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '0.35rem',
        userSelect: 'none',
        overflow: 'hidden',
      }}>
        <a
          href="https://grc.alvarotorresb.com"
          style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', textDecoration: 'none', cursor: 'pointer' }}
        >
          <span style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING,
            fontSize: '1rem',
            flexShrink: 0,
          }}>
            AT.
          </span>
          <span style={{
            ...FONT_BODY,
            color: BRAND_PRIMARY,
            fontSize: '0.9rem',
            flexShrink: 0,
          }}>
            · grc
          </span>
        </a>
        {orgName && (
          <>
            <span style={{
              ...FONT_BODY,
              color: BORDER_DEFAULT,
              fontSize: '0.9rem',
              flexShrink: 0,
              margin: '0 0.15rem',
            }}>
              |
            </span>
            <span style={{
              ...FONT_BODY,
              color: TEXT_MUTED,
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '22ch',
            }}>
              {orgName}
            </span>
          </>
        )}
      </div>

      {/* Language switcher */}
      <div style={{
        display: 'flex',
        border: `1px solid ${BORDER_DEFAULT}`,
        borderRadius: 6,
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {[
          { code: 'es', label: 'ESPAÑOL' },
          { code: 'en', label: 'ENGLISH' },
        ].map(({ code, label }) => {
          const active = lang === code;
          return (
            <button
              key={code}
              onClick={() => onLangToggle(code)}
              style={{
                ...FONT_TINY,
                background: active ? TEXT_HEADING : 'transparent',
                color: active ? SURFACE_WHITE : TEXT_MUTED,
                border: 'none',
                borderRadius: 0,
                padding: '0.3rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.62rem',
                letterSpacing: '0.06em',
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── MODULE SHELL ─────────────────────────────────────────────────────────────
// Shared page wrapper applied to every module route. Keeps the content area
// width and background consistent as the user navigates between modules.
function ModuleShell({ children }) {
  return (
    <div style={{
      minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      background: BG_CONTENT,
      padding: '2.5rem 1.5rem',
      boxSizing: 'border-box',
    }}>
      <div style={{ maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto' }}>
        {children}
      </div>
    </div>
  );
}

// ─── APP STATES ───────────────────────────────────────────────────────────────
// appState:  'initializing' | 'setup' | 'org-select' | 'dashboard'
// navView:   { view: 'dashboard' | 'risks' }   (active when appState='dashboard')

// ─── UNSAVED CHANGES GUARD TEXTS ──────────────────────────────────────────────
const GUARD_T = {
  es: {
    unsavedConfirm:     'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    cancel:             'Cancelar',
  },
  en: {
    unsavedConfirm:     'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    cancel:             'Cancel',
  },
};

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
export default function App() {
  const [lang, setLang] = useState('en');
  const [appState, setAppState] = useState('initializing');
  const [activeOrg, setActiveOrg] = useState(null);       // full profile.json
  const [navView, setNavView] = useState({ view: 'dashboard' });

  // ── Dirty-state guard ────────────────────────────────────────────────────
  // dirtyRef is a ref (not state) so children can update it from callbacks
  // without triggering App re-renders. Each module propagates its own dirty
  // state up through the onDirtyChange prop.
  const dirtyRef = useRef(false);
  const [pendingNav, setPendingNav] = useState(null);
  const [confirmNav, setConfirmNav] = useState(false);
  // saveRequestTick is incremented when the user picks "Save and leave" in
  // the App-level guard. Active views observe the increase via a useEffect
  // and trigger their internal save flow, then report success/failure back
  // through the onSaveResult prop. Per GRC_UI_PATTERNS.md §5.
  const [saveRequestTick, setSaveRequestTick] = useState(0);

  const handleDirtyChange = (isDirty) => {
    dirtyRef.current = isDirty;
  };

  // ── Sync document root lang with UI lang ─────────────────────────────────
  // Mirrors the `lang` state into `document.documentElement.lang` so that
  // native browser elements (e.g. `<input type="date">`) render placeholders
  // in the active UI language instead of the OS locale.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // ── Bootstrap ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        // Verify the stored handle has (or can obtain) readwrite permission.
        // verifyRootHandle handles queryPermission + requestPermission for
        // 'prompt' state internally and returns a simple boolean.
        const ok = await verifyRootHandle();
        if (!ok) {
          // Handle is absent, stale, or permission was denied — clear any
          // leftover entry so the user starts fresh with the folder picker.
          await clearRootHandle().catch(() => {});
          setAppState('setup');
          return;
        }
        const storedId = localStorage.getItem('grc-active-org');
        if (!storedId) { setAppState('org-select'); return; }
        try {
          await runEvidenceMigrationIfNeeded(storedId);
          await runDateFormatMigrationIfNeeded(storedId);
          const profile = await readOrgFile(storedId, 'profile.json');
          setActiveOrg(profile);
          setAppState('dashboard');
        } catch {
          localStorage.removeItem('grc-active-org');
          setAppState('org-select');
        }
      } catch {
        setAppState('setup');
      }
    })();
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleRootReady = () => setAppState('org-select');

  const handleOrgSelect = async (orgId, profile) => {
    try {
      await runEvidenceMigrationIfNeeded(orgId);
      await runDateFormatMigrationIfNeeded(orgId);
    } catch {
      localStorage.removeItem('grc-active-org');
      setAppState('org-select');
      return;
    }
    // Re-read profile.json after the sweep so the in-memory copy reflects the
    // migration flags and any cleared evidence ids.
    let mergedProfile = profile;
    try {
      mergedProfile = await readOrgFile(orgId, 'profile.json');
    } catch {
      // Keep the originally-passed profile if re-read fails — the sweep was
      // already idempotent and persistent.
    }
    localStorage.setItem('grc-active-org', orgId);
    setActiveOrg(mergedProfile);
    setNavView({ view: 'dashboard' });
    dirtyRef.current = false;
    setAppState('dashboard');
  };

  const handleSwitchOrg = () => {
    localStorage.removeItem('grc-active-org');
    setActiveOrg(null);
    setNavView({ view: 'dashboard' });
    dirtyRef.current = false;
    setAppState('org-select');
  };

  const handleNavigate = (moduleId, params) => {
    if (dirtyRef.current) {
      setPendingNav({ view: moduleId, params: params || null });
      setConfirmNav(true);
      return;
    }
    dirtyRef.current = false;
    setNavView({ view: moduleId, params: params || null });
  };

  const handleBackToDashboard = () => setNavView({ view: 'dashboard' });

  const handleConfirmLeave = () => {
    const target = pendingNav;
    setConfirmNav(false);
    setPendingNav(null);
    dirtyRef.current = false;
    if (target !== null) setNavView({ view: target.view, params: target.params || null });
  };

  const handleCancelLeave = () => {
    setConfirmNav(false);
    setPendingNav(null);
  };

  // "Save and leave": close the dialog, keep pendingNav, increment the
  // tick. The active view observes the tick, runs its save, and calls
  // onSaveResult with the boolean outcome (see handleSaveResult below).
  const handleSaveAndLeave = () => {
    setConfirmNav(false);
    setSaveRequestTick(n => n + 1);
  };

  // Result callback from the active view's save flow after a tick-driven
  // save. On success: navigate to the queued destination and clear the
  // dirty signal. On error: keep the user on the view (the view's own
  // error banner is already showing) and clear the queued destination.
  const handleSaveResult = (ok) => {
    if (ok) {
      const target = pendingNav;
      setPendingNav(null);
      dirtyRef.current = false;
      if (target !== null) setNavView({ view: target.view, params: target.params || null });
    } else {
      setPendingNav(null);
    }
  };

  const handleOrgUpdated = (updatedOrg) => setActiveOrg(updatedOrg);

  // ── Loading splash ────────────────────────────────────────────────────────
  if (appState === 'initializing') return null;

  const onDashboard = appState === 'dashboard' && activeOrg;

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{AI_STYLES}</style>
      <style>{FONTS}</style>
      <Navbar
        lang={lang}
        onLangToggle={setLang}
        orgName={onDashboard ? activeOrg.name : null}
      />

      {appState === 'setup' && (
        <SetupScreen lang={lang} onReady={handleRootReady} />
      )}

      {appState === 'org-select' && (
        <OrgSelector lang={lang} onSelect={handleOrgSelect} />
      )}

      {onDashboard && (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Sidebar
            activeView={navView.view}
            onNavigate={handleNavigate}
            lang={lang}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            {navView.view === 'dashboard' && (
              <Dashboard
                lang={lang}
                org={activeOrg}
                onSwitchOrg={handleSwitchOrg}
                onNavigate={handleNavigate}
                onOrgUpdated={handleOrgUpdated}
              />
            )}
            {navView.view === 'context' && (
              <ModuleShell>
                <ContextView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'scope' && (
              <ModuleShell>
                <ScopeView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'policy' && (
              <ModuleShell>
                <DocumentsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} typeFilter="Policy" />
              </ModuleShell>
            )}
            {navView.view === 'objectives' && (
              <ModuleShell>
                <ObjectivesView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'risks' && (
              <ModuleShell>
                <RiskList
                  lang={lang}
                  org={activeOrg}
                  onBack={handleBackToDashboard}
                  onNavigate={handleNavigate}
                  onDirtyChange={handleDirtyChange}
                  saveRequestTick={saveRequestTick}
                  onSaveResult={handleSaveResult}
                  initialEditId={navView.params?.editId || null}
                  initialViewId={navView.params?.viewId || null}
                />
              </ModuleShell>
            )}
            {navView.view === 'soa' && (
              <ModuleShell>
                <SoAView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'rtp' && (
              <ModuleShell>
                <RTPView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'settings' && (
              <ModuleShell>
                <SettingsView
                  lang={lang}
                  org={activeOrg}
                  initialSection={navView.params?.section || null}
                  onBack={handleBackToDashboard}
                  onOrgUpdated={handleOrgUpdated}
                  onDirtyChange={handleDirtyChange}
                  saveRequestTick={saveRequestTick}
                  onSaveResult={handleSaveResult}
                />
              </ModuleShell>
            )}
            {navView.view === 'audits' && (
              <ModuleShell>
                <AuditsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'management-reviews' && (
              <ModuleShell>
                <ManagementReviewsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'nonconformities' && (
              <ModuleShell>
                <NonconformitiesView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} initialPrefill={navView.params?.prefill || null} />
              </ModuleShell>
            )}
            {navView.view === 'assets' && (
              <ModuleShell>
                <AssetsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'suppliers' && (
              <ModuleShell>
                <SuppliersView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'customers' && (
              <ModuleShell>
                <CustomersView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'incidents' && (
              <ModuleShell>
                <IncidentsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'documents' && (
              <ModuleShell>
                <DocumentsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} initialEditId={navView.params?.editId || null} />
              </ModuleShell>
            )}
            {navView.view === 'competence' && (
              <ModuleShell>
                <CompetenceView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'awareness' && (
              <ModuleShell>
                <AwarenessView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'metrics' && (
              <ModuleShell>
                <MetricsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'authorities' && (
              <ModuleShell>
                <AuthoritiesView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'special-interest-groups' && (
              <ModuleShell>
                <SpecialInterestGroupsView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'legal-register' && (
              <ModuleShell>
                <LegalRegisterView lang={lang} org={activeOrg} onBack={handleBackToDashboard} onNavigate={handleNavigate} onDirtyChange={handleDirtyChange} saveRequestTick={saveRequestTick} onSaveResult={handleSaveResult} />
              </ModuleShell>
            )}
            {navView.view === 'executive-dashboard' && (
              <ExecutiveDashboardView
                lang={lang}
                org={activeOrg}
                onBack={handleBackToDashboard}
              />
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmNav}
        message={(GUARD_T[lang] || GUARD_T.en).unsavedConfirm}
        confirmLabel={(GUARD_T[lang] || GUARD_T.en).leaveWithoutSaving}
        cancelLabel={(GUARD_T[lang] || GUARD_T.en).cancel}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
        secondaryAction={{
          label:   (GUARD_T[lang] || GUARD_T.en).saveAndLeave,
          onClick: handleSaveAndLeave,
        }}
      />
    </>
  );
}
